import { inject, Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import {
  deleteDoc,
  doc,
  DocumentReference,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import {
  cloudFunctionNames,
  IEntity,
  IEntityDetails,
  IHttpsCallableResponse,
  isProEntity,
  UUID
} from 'src/app/core/interfaces';
import { IEntityDetailsCf, IUpdateEntityDetailsWithTransferReq } from '@cloud-functions/interfaces';
import { convertDatesToTimestamps, convertTimestampsToDates } from 'src/app/core/utils/helper-functions';
import { httpsCallable } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';

interface IEntityDetailsService {
  deleteEntityDetails(entityId: UUID): Promise<void>;
  updateWithEntityTransfer(transferredEntity: IEntity): Promise<IEntityDetails>;
}

@Injectable({
  providedIn: 'root'
})
export class EntityDetailsService implements IEntityDetailsService {
  private firestore: Firestore = inject(Firestore);
  protected functions: Functions = inject(Functions);

  private convertEntityDetailsToCf(entityDetails: IEntityDetails): IEntityDetailsCf {
    return convertDatesToTimestamps(entityDetails) as IEntityDetailsCf;
  }

  private convertEntityDetailsFromCf(entityDetails: IEntityDetailsCf): IEntityDetails {
    return convertTimestampsToDates(entityDetails) as IEntityDetails;
  }

  private getEntityDetailsDoc(entityId: UUID): DocumentReference<IEntityDetailsCf> {
    return doc(
      this.firestore,
      "entityDetails",
      entityId
    ) as DocumentReference<IEntityDetailsCf>;
  }

  public async getEntityDetails(entityId: UUID): Promise<IEntityDetails> {
    const entityDetailsDoc = this.getEntityDetailsDoc(entityId);
    const entityDetailsSnap = await getDoc(entityDetailsDoc);

    if (!entityDetailsSnap.exists()) {
      throw new Error("Entity details not found");
    }

    return this.convertEntityDetailsFromCf(entityDetailsSnap.data());
  }

  public async updateEntityDetails(entityId: UUID, entityDetails: IEntityDetails): Promise<IEntityDetails> {
    const entityDetailsDoc = this.getEntityDetailsDoc(entityId);
    const entityDetailsSnap = await getDoc(entityDetailsDoc);
    if (!entityDetailsSnap.exists) {
      throw new Error("Entity details not found");
    }
    await setDoc(entityDetailsDoc, this.convertEntityDetailsToCf(entityDetails));
    return entityDetails;
  }

  public async updateWithEntityTransfer(transferredEntity: IEntity): Promise<IEntityDetails> {
    console.log("Updating entity details with entity transfer", transferredEntity.id);
    if (!isProEntity(transferredEntity)) {
      throw new Error("Invalid entity type, expected secure-pro entity")
    }
    const updateEntityDetailsWithTransferCallable = httpsCallable<
      IUpdateEntityDetailsWithTransferReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.secureFileTransfer.updateEntityDetailsWithTransfer);

    const response = await updateEntityDetailsWithTransferCallable({
      orgId: transferredEntity.managingOrgId,
      entityId: transferredEntity.id,
    });

    if (!response.data.success) {
      throw new Error(response.data.message);
    }

    return this.getEntityDetails(transferredEntity.id);
  }

  public async updateWithInstantEntityTransfer(transferredEntity: IEntity): Promise<IEntityDetails> {
    console.log("Updating entity details with instant entity transfer", transferredEntity.id);
    if (!isProEntity(transferredEntity)) {
      throw new Error("Invalid entity type, expected secure-pro entity")
    }
    const updateEntityDetailsWithInstantTransferCallable = httpsCallable<
      IUpdateEntityDetailsWithTransferReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.secureFileTransfer.updateEntityDetailsWithInstantTransfer);

    const response = await updateEntityDetailsWithInstantTransferCallable({
      orgId: transferredEntity.managingOrgId,
      entityId: transferredEntity.id,
    });

    if (!response.data.success) {
      throw new Error(response.data.message);
    }

    return this.getEntityDetails(transferredEntity.id);
  }

  public async deleteEntityDetails(entityId: UUID): Promise<void> {
    console.log("Deleting entity details", entityId);
    // TODO: research how to delete entity details properly
    const entityDetailsDoc = this.getEntityDetailsDoc(entityId);
    return deleteDoc(entityDetailsDoc);
  }
}
